import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CLOManagerScorecardPanel from './CLOManagerScorecardPanel';
import CloDealOpsDrawer from './CloDealOpsDrawer';
import CloEquityMtmModal, { CloEquityPositionLite } from './CloEquityMtmModal';
import CloEquityDisposeModal from './CloEquityDisposeModal';
import CloTrancheDrawer, { CloDrawerPosition } from './CloTrancheDrawer';

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white; border: none;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4); }
  ` : `
    background: transparent; color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    &:hover { border-color: #10b981; color: white; }
  `}
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.$color || '#10b981'};
`;

const StatLabel = styled.div`
  color: #94a3b8; font-size: 0.7rem; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white; font-size: 1.5rem; font-weight: 700;
`;

const DealsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 1.5rem;
`;

const DealCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const DealHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem; border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const DealName = styled.div`
  color: white; font-weight: 600; font-size: 1rem;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 10px; border-radius: 6px;
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  ${props => {
    switch (props.$status) {
      case 'REINVESTMENT': return 'background: rgba(16,185,129,0.1); color: #10b981; border: 1px solid rgba(16,185,129,0.2);';
      case 'AMORTIZING': return 'background: rgba(245,158,11,0.1); color: #f59e0b; border: 1px solid rgba(245,158,11,0.2);';
      case 'WAREHOUSE': case 'RAMPING': return 'background: rgba(59,130,246,0.1); color: #3b82f6; border: 1px solid rgba(59,130,246,0.2);';
      case 'CALLED': case 'MATURED': return 'background: rgba(148,163,184,0.1); color: #94a3b8; border: 1px solid rgba(148,163,184,0.2);';
      default: return 'background: rgba(148,163,184,0.1); color: #94a3b8; border: 1px solid rgba(148,163,184,0.2);';
    }
  }}
`;

const DealBody = styled.div`
  padding: 1.25rem;
`;

const MetricsGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1rem;
`;

const MetricItem = styled.div``;

const MetricLabel = styled.div`
  color: #64748b; font-size: 0.65rem; text-transform: uppercase; margin-bottom: 2px;
`;

const MetricValue = styled.div<{ $highlight?: boolean; $warn?: boolean }>`
  color: ${props => props.$warn ? '#ef4444' : props.$highlight ? '#f59e0b' : 'white'};
  font-size: 1rem; font-weight: 700;
`;

const TestsSection = styled.div`
  margin-top: 1rem; padding-top: 1rem; border-top: 1px solid rgba(255,255,255,0.05);
`;

const TestsTitle = styled.div`
  color: #94a3b8; font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
  margin-bottom: 0.75rem; letter-spacing: 0.05em;
`;

const TestRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.4rem 0; border-bottom: 1px solid rgba(255,255,255,0.03);
`;

const TestName = styled.div`
  color: #94a3b8; font-size: 0.8rem;
`;

const TestValue = styled.div<{ $pass: boolean }>`
  color: ${props => props.$pass ? '#10b981' : '#ef4444'};
  font-size: 0.8rem; font-weight: 600;
`;

const TranchesPreview = styled.div`
  display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 1rem;
`;

const TrancheBadge = styled.span<{ $rating: string }>`
  padding: 4px 8px; border-radius: 4px; font-size: 0.65rem; font-weight: 600;
  background: ${props => {
    if (props.$rating.startsWith('Aaa') || props.$rating.startsWith('AAA')) return 'rgba(16,185,129,0.15)';
    if (props.$rating.startsWith('Aa') || props.$rating.startsWith('AA')) return 'rgba(59,130,246,0.15)';
    if (props.$rating.startsWith('A')) return 'rgba(139,92,246,0.15)';
    if (props.$rating.startsWith('Baa') || props.$rating.startsWith('BBB')) return 'rgba(245,158,11,0.15)';
    return 'rgba(239,68,68,0.15)';
  }};
  color: ${props => {
    if (props.$rating.startsWith('Aaa') || props.$rating.startsWith('AAA')) return '#10b981';
    if (props.$rating.startsWith('Aa') || props.$rating.startsWith('AA')) return '#3b82f6';
    if (props.$rating.startsWith('A')) return '#8b5cf6';
    if (props.$rating.startsWith('Baa') || props.$rating.startsWith('BBB')) return '#f59e0b';
    return '#ef4444';
  }};
`;

const RunTestButton = styled.button`
  padding: 0.4rem 0.75rem; border-radius: 6px; font-size: 0.7rem; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  background: rgba(16,185,129,0.1); color: #10b981; border: 1px solid rgba(16,185,129,0.2);
  &:hover { background: rgba(16,185,129,0.2); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

interface CoverageTest {
  trancheName: string;
  trancheType: string;
  ocActual: number;
  ocTrigger: number;
  ocCushion: number;
  ocStatus: string;
  icActual: number;
  icTrigger: number;
  icCushion: number;
  icStatus: string;
}

interface DealMetrics {
  dealId: number;
  warf: number;
  was: number;
  wal: number;
  diversityScore: number;
  cccBucketPct: number;
  collateralPar: number;
  defaultedPar: number;
  totalTrancheBalance: number;
  collateralCount: number;
  defaultedCount: number;
  coverageTests: CoverageTest[];
  metricsSource?: string;
}

interface CLODeal {
  id: number;
  dealName: string;
  dealType: string;
  status: string;
  managerName: string;
  originalDealSize: number;
  currentDealSize: number;
  warf: number;
  was: number;
  wal: number;
  diversityScore: number;
  cccBucketPct: number;
  collateralPar: number;
  tranches: { trancheName: string; trancheType: string; moodysRating: string; spRating: string; currentBalance: number; spreadBps: number }[];
}

interface CLOManagementScreenProps {
  embedded?: boolean;
}

type CLOView = 'deals' | 'positions' | 'managers';

const PositionsWrap = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const PositionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th, td { padding: 0.75rem 1rem; text-align: left; }
  thead th {
    color: #94a3b8;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(15, 23, 42, 0.5);
  }
  tbody td {
    color: #e2e8f0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  }
  tbody tr:hover td { background: rgba(255, 255, 255, 0.02); }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

const RowAction = styled.button<{ $variant: 'primary' | 'danger' }>`
  padding: 0.35rem 0.7rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  margin-right: 0.4rem;
  border: 1px solid;
  ${p => p.$variant === 'primary' ? `
    background: rgba(139, 92, 246, 0.1);
    color: #c4b5fd;
    border-color: rgba(139, 92, 246, 0.3);
    &:hover { background: rgba(139, 92, 246, 0.2); }
  ` : `
    background: rgba(239, 68, 68, 0.08);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.3);
    &:hover { background: rgba(239, 68, 68, 0.18); }
  `}
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

interface CloEquityPositionRow {
  positionId: number;
  securityId: number;
  cusip: string;
  securityName: string;
  portfolioId: number;
  portfolioName: string;
  quantity: number;
  costBasis: number;
  marketValue: number;
  unrealizedPnl: number;
  asOfDate?: string;
}

// (PositionsTable + view-toggle styles above)
const ViewToggle = styled.div`
  display: inline-flex;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 3px;
`;

const ViewBtn = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  font-size: 0.78rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 700;
  border: 0;
  border-radius: 5px;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(16, 185, 129, 0.1))' : 'transparent')};
  color: ${(p) => (p.$active ? '#86efac' : '#94a3b8')};
  cursor: pointer;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #e2e8f0;
  }
`;

const CLOManagementScreen: React.FC<CLOManagementScreenProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [view, setView] = useState<CLOView>('deals');
  const [deals, setDeals] = useState<CLODeal[]>([]);
  const [metricsMap, setMetricsMap] = useState<Record<number, DealMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [runningTests, setRunningTests] = useState<number | null>(null);
  const [opsDeal, setOpsDeal] = useState<{ id: number; name?: string } | null>(null);

  // Equity positions tab — list of CLO Equity holdings with MTM / Dispose actions
  const [positions, setPositions] = useState<CloEquityPositionRow[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [mtmTarget, setMtmTarget] = useState<CloEquityPositionLite | null>(null);
  const [disposeTarget, setDisposeTarget] = useState<CloEquityPositionLite | null>(null);
  const [drawerTarget, setDrawerTarget] = useState<CloDrawerPosition | null>(null);

  const fetchPositions = useCallback(async () => {
    try {
      setPositionsLoading(true);
      // Pull latest positions, filter client-side to CLO_EQUITY. Backend
      // already exposes /api/positions/latest with assetSubClassName on the
      // DTO, so no new endpoint is required for the v1 dashboard view.
      const res = await axios.get('/api/positions/latest');
      const raw: any[] = Array.isArray(res.data) ? res.data : [];
      const rows: CloEquityPositionRow[] = raw
        .filter(p => {
          // F-CLO-009 (CLO E2E 2026-08-29): the equity-only filter hid the
          // firm's actual CLO book ($30M of mezz was invisible). ANY CLO
          // tranche position belongs on this tab — match every CLO_* asset
          // class and 'CLO ...' securityType variant the platform mints.
          const cls = (p.assetClassName || '').toUpperCase();
          const sub = (p.assetSubClassName || '').toUpperCase();
          const typ = (p.securityType || '').toUpperCase();
          return cls.startsWith('CLO') || sub.startsWith('CLO') || typ.startsWith('CLO');
        })
        .filter(p => Number(p.quantity) !== 0)
        .map(p => ({
          positionId: Number(p.id) || 0,
          securityId: Number(p.securityId),
          cusip: p.cusip || '',
          securityName: p.securityName || p.cusip || 'CLO Equity',
          portfolioId: Number(p.portfolioId),
          portfolioName: p.portfolioName || '',
          quantity: Number(p.quantity) || 0,
          costBasis: Number(p.costBasis) || 0,
          marketValue: Number(p.marketValue) || 0,
          unrealizedPnl: Number(p.unrealizedPnl) || 0,
          asOfDate: p.asOfDate,
        }));
      setPositions(rows);
    } catch (err) {
      console.error('Failed to fetch CLO tranche positions:', err);
      setPositions([]);
    } finally {
      setPositionsLoading(false);
    }
  }, []);

  useEffect(() => { fetchDeals(); }, []);
  useEffect(() => { if (view === 'positions') fetchPositions(); }, [view, fetchPositions]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/credit/clo/deals');
      const data = Array.isArray(response.data) ? response.data : [];
      setDeals(data);

      // Fetch calculated metrics for each deal
      const metricsEntries: Record<number, DealMetrics> = {};
      await Promise.all(data.map(async (deal: CLODeal) => {
        try {
          const mRes = await axios.get(`/api/credit/clo/deals/${deal.id}/metrics`);
          if (mRes.data) metricsEntries[deal.id] = mRes.data;
        } catch { /* metrics not available */ }
      }));
      setMetricsMap(metricsEntries);
    } catch (err) {
      console.error('Failed to fetch CLO deals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunTests = async (dealId: number) => {
    setRunningTests(dealId);
    try {
      const res = await axios.post(`/api/credit/clo/deals/${dealId}/run-tests`);
      if (res.data) {
        setMetricsMap(prev => ({ ...prev, [dealId]: res.data }));
      }
      // Refresh deals to get updated stored metrics
      const dealsRes = await axios.get('/api/credit/clo/deals');
      setDeals(Array.isArray(dealsRes.data) ? dealsRes.data : []);
    } catch (err) {
      console.error('Failed to run tests:', err);
    } finally {
      setRunningTests(null);
    }
  };

  const formatCurrency = (value: number | undefined | null) => {
    if (!value) return '$0';
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const safeDeals = Array.isArray(deals) ? deals : [];
  const totalAUM = safeDeals.reduce((sum, d) => sum + (d.currentDealSize || 0), 0);
  const activeDeals = safeDeals.filter(d => d.status === 'REINVESTMENT' || d.status === 'AMORTIZING').length;
  const warfDeals = safeDeals.filter(d => d.warf && d.warf > 0);
  const avgWarf = warfDeals.length > 0 ? warfDeals.reduce((sum, d) => sum + d.warf, 0) / warfDeals.length : 0;

  return (
    <Container $embedded={embedded}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: embedded ? '1rem' : 0, gap: '1rem', flexWrap: 'wrap' }}>
        {!embedded && (
          <Title>CLO Management</Title>
        )}
        {embedded && <div />}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <ViewToggle>
            <ViewBtn $active={view === 'deals'} onClick={() => setView('deals')}>Deals</ViewBtn>
            <ViewBtn $active={view === 'positions'} onClick={() => setView('positions')}>Tranche Positions</ViewBtn>
            <ViewBtn $active={view === 'managers'} onClick={() => setView('managers')}>Manager Scorecard</ViewBtn>
          </ViewToggle>
          {view === 'deals' && (
            <Actions>
              <Button $variant="secondary" onClick={() => safeDeals.forEach(d => handleRunTests(d.id))}>
                Run All Tests
              </Button>
              <Button $variant="primary" onClick={() => navigate('/trade-entry/clo-equity')}>
                + New CLO Equity Ticket
              </Button>
            </Actions>
          )}
        </div>
      </div>

      {view === 'managers' ? (
        <CLOManagerScorecardPanel />
      ) : view === 'positions' ? (
        <PositionsWrap>
          <PositionsTable>
            <thead>
              <tr>
                <th>Security</th>
                <th>CUSIP</th>
                <th>Portfolio</th>
                <th className="num">Units</th>
                <th className="num">Cost Basis</th>
                <th className="num">Market Value</th>
                <th className="num">Unrealised P&amp;L</th>
                <th style={{ width: '230px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {positionsLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>Loading positions…</td></tr>
              )}
              {!positionsLoading && positions.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                  No CLO tranche positions yet. Acquisitions originate via the CLO Tranche ticket or doc-ingestion (CLO_OFFERING_MEMO).
                </td></tr>
              )}
              {!positionsLoading && positions.map(p => {
                const lite: CloEquityPositionLite = {
                  securityId: p.securityId,
                  cusip: p.cusip,
                  dealName: p.securityName,
                  trancheName: undefined,
                  quantity: p.quantity,
                  investmentAmount: p.costBasis,
                  marketValue: p.marketValue,
                  portfolioName: p.portfolioName,
                };
                return (
                  <tr
                    key={`${p.positionId}-${p.securityId}`}
                    style={{ cursor: 'pointer' }}
                    title="View tranche detail"
                    onClick={() => setDrawerTarget(p)}
                  >
                    <td>{p.securityName}</td>
                    <td style={{ fontFamily: 'monospace', color: '#94a3b8' }}>{p.cusip}</td>
                    <td>{p.portfolioName}</td>
                    <td className="num">{p.quantity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="num">${p.costBasis.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="num" style={{ color: '#60a5fa', fontWeight: 600 }}>
                      ${p.marketValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="num" style={{ color: p.unrealizedPnl >= 0 ? '#34d399' : '#f87171', fontWeight: 600 }}>
                      {p.unrealizedPnl >= 0 ? '+' : ''}${p.unrealizedPnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td>
                      <RowAction $variant="primary" onClick={(e) => { e.stopPropagation(); setMtmTarget(lite); }}>Mark NAV</RowAction>
                      <RowAction $variant="danger" onClick={(e) => { e.stopPropagation(); setDisposeTarget(lite); }}>Dispose</RowAction>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </PositionsTable>
        </PositionsWrap>
      ) : (
      <>
      <StatsRow>
        <StatCard $color="#10b981">
          <StatLabel>Total AUM</StatLabel>
          <StatValue>{formatCurrency(totalAUM)}</StatValue>
        </StatCard>
        <StatCard $color="#3b82f6">
          <StatLabel>Active Deals</StatLabel>
          <StatValue>{activeDeals}</StatValue>
        </StatCard>
        <StatCard $color="#8b5cf6">
          <StatLabel>Avg WARF</StatLabel>
          <StatValue>{avgWarf.toFixed(0)}</StatValue>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatLabel>Total Deals</StatLabel>
          <StatValue>{safeDeals.length}</StatValue>
        </StatCard>
      </StatsRow>

      <DealsGrid>
        {safeDeals.map(deal => {
          const metrics = metricsMap[deal.id];
          const warf = metrics?.warf ?? deal.warf ?? 0;
          const was = metrics?.was ?? deal.was ?? 0;
          const wal = metrics?.wal ?? deal.wal ?? 0;
          const diversity = metrics?.diversityScore ?? deal.diversityScore ?? 0;
          const cccPct = metrics?.cccBucketPct ?? deal.cccBucketPct ?? 0;
          const coverageTests = metrics?.coverageTests || [];
          const isActive = deal.status === 'REINVESTMENT' || deal.status === 'AMORTIZING';

          return (
            <DealCard key={deal.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <DealHeader>
                <DealName>{deal.dealName}</DealName>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <StatusBadge $status={deal.status}>{deal.status}</StatusBadge>
                  <RunTestButton onClick={() => handleRunTests(deal.id)} disabled={runningTests === deal.id}>
                    {runningTests === deal.id ? 'Running...' : 'Run Tests'}
                  </RunTestButton>
                  <RunTestButton onClick={() => setOpsDeal({ id: deal.id, name: deal.dealName })}>
                    Deal Ops
                  </RunTestButton>
                </div>
              </DealHeader>
              <DealBody>
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>WARF</MetricLabel>
                    <MetricValue $highlight={warf > 3000}>{warf.toFixed ? warf.toFixed(0) : warf}</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>WAS</MetricLabel>
                    <MetricValue>{was}bp</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>WAL</MetricLabel>
                    <MetricValue>{typeof wal === 'number' ? wal.toFixed(1) : wal}y</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Diversity</MetricLabel>
                    <MetricValue>{diversity}</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>CCC Bucket</MetricLabel>
                    <MetricValue $warn={cccPct > 7.5}>{typeof cccPct === 'number' ? cccPct.toFixed(1) : cccPct}%</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Size</MetricLabel>
                    <MetricValue>{formatCurrency(deal.currentDealSize)}</MetricValue>
                  </MetricItem>
                </MetricsGrid>

                {metrics?.collateralCount != null && (
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                    <span>Collateral: {metrics.collateralCount} loans</span>
                    <span>Par: {formatCurrency(metrics.collateralPar)}</span>
                    {metrics.metricsSource && (
                      <span style={{ color: metrics.metricsSource.startsWith('STORED') ? '#f59e0b' : '#64748b' }}>
                        {metrics.metricsSource}
                      </span>
                    )}
                    {metrics.defaultedCount > 0 && (
                      <span style={{ color: '#ef4444' }}>Defaulted: {metrics.defaultedCount} ({formatCurrency(metrics.defaultedPar)})</span>
                    )}
                  </div>
                )}

                {/* Coverage Tests - calculated from collateral */}
                {coverageTests.length > 0 && (
                  <TestsSection>
                    <TestsTitle>Coverage Tests</TestsTitle>
                    {coverageTests.map((test, idx) => (
                      <div key={idx}>
                        <TestRow>
                          <TestName>{test.trancheName} O/C</TestName>
                          <TestValue $pass={test.ocStatus === 'PASS'}>
                            {test.ocActual?.toFixed(3)}x / {test.ocTrigger?.toFixed(3)}x (cushion: {test.ocCushion?.toFixed(3)})
                            {test.ocStatus === 'PASS' ? ' PASS' : ' FAIL'}
                          </TestValue>
                        </TestRow>
                        <TestRow>
                          <TestName>{test.trancheName} I/C</TestName>
                          <TestValue $pass={test.icStatus === 'PASS'}>
                            {test.icActual?.toFixed(3)}x / {test.icTrigger?.toFixed(3)}x (cushion: {test.icCushion?.toFixed(3)})
                            {test.icStatus === 'PASS' ? ' PASS' : ' FAIL'}
                          </TestValue>
                        </TestRow>
                      </div>
                    ))}
                  </TestsSection>
                )}

                {/* Tranches */}
                {deal.tranches && deal.tranches.length > 0 && (
                  <TranchesPreview>
                    {deal.tranches.map((tranche, idx) => (
                      <TrancheBadge key={idx} $rating={tranche.moodysRating || tranche.spRating || 'NR'}>
                        {tranche.trancheName}: {tranche.moodysRating || tranche.spRating || 'NR'}
                      </TrancheBadge>
                    ))}
                  </TranchesPreview>
                )}
              </DealBody>
            </DealCard>
          );
        })}
      </DealsGrid>
      </>
      )}

      {mtmTarget && (
        <CloEquityMtmModal
          position={mtmTarget}
          onClose={() => setMtmTarget(null)}
          onSuccess={() => { setMtmTarget(null); fetchPositions(); }}
        />
      )}
      {disposeTarget && (
        <CloEquityDisposeModal
          position={disposeTarget}
          onClose={() => setDisposeTarget(null)}
          onSuccess={() => { setDisposeTarget(null); fetchPositions(); }}
        />
      )}
      <CloTrancheDrawer position={drawerTarget} onClose={() => setDrawerTarget(null)} />
          <CloDealOpsDrawer dealId={opsDeal?.id ?? null} dealName={opsDeal?.name}
        onClose={() => setOpsDeal(null)} />
    </Container>
  );
};

export default CLOManagementScreen;
